import React, { forwardRef, useEffect, useState } from 'react';
import { Box, CircularProgress, darken, lighten, List, ListItem, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { faCheck } from '@fortawesome/sharp-regular-svg-icons';
import { FlexyFilterValue, FlexyFilterWidgetProps, SelectApiFilter } from '../DatatableHeader';
import { FlexyFilterSearchInput } from '../FlexyFilterSearchInput';

export const FlexySelectApiFilter: React.FunctionComponent = forwardRef(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    <T extends Record<string, unknown>>(props: FlexyFilterWidgetProps, ref) => {
        const { filter, value, onChange } = props;
        const [items, setItems] = useState<Array<T>>([]);
        const [searchTerm, setSearchTerm] = useState<string>('');
        const [loading, setLoading] = useState<boolean>(false);
        const [selectValue, setSelectValue] = useState<FlexyFilterValue>(null);
        const [selectValues, setSelectValues] = useState<FlexyFilterValue>([]);
        const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
        const { palette } = useTheme();
        const isLightMode = palette.mode === 'light';
        const selectApiFilter = filter as SelectApiFilter<T>;
        const {
            icon,
            showResultOnSearchOnly,
            searchFunction,
            valueMap,
            renderIcon,
            renderItem,
            multiple = false,
        } = selectApiFilter;

        useEffect(() => {
            setSelectValue(value);
        }, [value]);

        useEffect(() => {
            if (showResultOnSearchOnly && searchTerm === '') {
                setItems([]);
                return;
            }

            setLoading(true);
            searchFunction(searchTerm).then((data: Array<T>) => {
                setItems(data);
                setLoading(false);
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [searchTerm]);

        const handleSearchTermChanged = (term: string) => {
            setSearchTerm(term);
        };

        const handleSelectItem = (item: unknown) => {
            // Mulitple selection on list
            if (multiple) {
                const itemValue = valueMap(item) as string;
                if (Array.isArray(selectValues))
                    if (selectValues.includes(itemValue)) {
                        // Check if value is allready selected to remove it from state
                        setSelectValues(
                            selectValues.filter((selectedValue) => selectedValue !== itemValue),
                        );
                    } else {
                        setSelectValues([...selectValues, itemValue]);
                    }
            } else {
                setSelectValue(valueMap(item));
                onChange(valueMap(item), item);
            }
        };

        useEffect(() => {
            if (multiple) {
                if (selectValues) {
                    onChange(selectValues, selectValues);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [selectValues]);

        return (
            <Box
                sx={{
                    width: '100%',
                    padding: '10px',
                }}
            >
                <FlexyFilterSearchInput
                    placeholder={filter.label}
                    searchTerm={searchTerm}
                    onSearchTermUpdate={handleSearchTermChanged}
                />
                {(items.length > 0 || loading) && (
                    <Box
                        sx={{
                            padding: '5px 0',
                            overflowY: 'scroll',
                            maxHeight: '220px',
                        }}
                    >
                        <Box
                            sx={{
                                color: palette.text.primary,
                                padding: '0px 14px',
                                fontSize: '14px',
                                fontWeight: '500',
                                lineHeight: '25.2px',
                                textAlign: 'left',
                                marginBottom: '5px',
                            }}
                        >
                            <Box>{filter.label}</Box>
                            {loading === true && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <CircularProgress
                                        sx={{
                                            color: isLightMode
                                                ? palette.secondary.dark
                                                : palette.secondary.light,
                                        }}
                                    />
                                </Box>
                            )}
                            {loading === false && items.length > 0 && (
                                <List>
                                    {items.map((item: unknown, index: number) => {
                                        let selected = false;
                                        if (multiple) {
                                            if (Array.isArray(selectValues)) {
                                                selected = selectValues.includes(valueMap(item));
                                            }
                                        } else {
                                            selected = selectValue === valueMap(item);
                                        }

                                        const isHovered = hoveredIndex === index;
                                        const hoverBg = isLightMode
                                            ? palette.grey[100]
                                            : palette.grey[800];
                                        const selectedBgColor = isLightMode
                                            ? lighten(palette.primary.main, 0.85)
                                            : darken(palette.primary.main, 0.5);
                                        const hoverSelectedBgColor = isLightMode
                                            ? lighten(palette.primary.main, 0.7)
                                            : darken(palette.primary.main, 0.4);

                                        let itemBgColor = 'transparent';
                                        if (isHovered && selected) {
                                            itemBgColor = hoverSelectedBgColor;
                                        } else if (isHovered) {
                                            itemBgColor = hoverBg;
                                        } else if (selected) {
                                            itemBgColor = selectedBgColor;
                                        }

                                        return (
                                            <ListItem
                                                onClick={() => handleSelectItem(item)}
                                                onMouseEnter={() => setHoveredIndex(index)}
                                                onMouseLeave={() => setHoveredIndex(null)}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                sx={{
                                                    backgroundColor: itemBgColor,
                                                    display: 'flex',
                                                    justifyContent: 'flex-start',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    gap: '0 12px',
                                                    transition:
                                                        'background-color ease-in-out 300ms',
                                                }}
                                            >
                                                {icon && !renderIcon && (
                                                    <FaOptionIcon
                                                        sx={{ fontSize: '12px', lineHeight: '0' }}
                                                        icon={['fasl', icon]}
                                                    />
                                                )}
                                                {renderIcon && renderIcon(item, icon)}
                                                {renderItem(item)}
                                                {selected && (
                                                    <Box sx={{ marginLeft: 'auto' }}>
                                                        <FaOptionIcon
                                                            sx={{
                                                                color: selected
                                                                    ? palette.primary.main
                                                                    : 'transparent',
                                                            }}
                                                            icon={faCheck}
                                                        />
                                                    </Box>
                                                )}
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    },
);
